import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import API, { UPLOADS_URL } from '../api/axios';

const SITE_NAME = 'St. Andrews World School, Sonipat';
const DEFAULT_SITE_TITLE = 'St. Andrews World School | Best CBSE School in Sonipat';
const DEFAULT_DESC =
  'St. Andrews World School, Sonipat is a premier CBSE-affiliated educational institution offering world-class infrastructure, experienced faculty, and holistic child development from Pre-Nursery to Grade XII.';
const DEFAULT_KEYWORDS =
  'St Andrews World School, SAWS Sonipat, Best School in Sonipat, CBSE School Sonipat, Top CBSE School Haryana, Admissions 2026-27';
const SITE_URL =
  process.env.REACT_APP_SITE_URL || 'https://www.sawssonipat.com';

const DEFAULT_IMAGE = `${SITE_URL}/images/logo-sonipat.png`;

const DEFAULT_SCHOOL_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'School',
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/images/logo-sonipat.png`,
  image: `${SITE_URL}/images/logo-sonipat.png`,
  description: DEFAULT_DESC,
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Block C, Ansal Sushant City, Sector 61, Kundli',
    addressLocality: 'Sonipat',
    addressRegion: 'Haryana',
    postalCode: '131028',
    addressCountry: 'IN',
  },
  telephone: '+91-9266333940',
  email: 'info@sawssonipat.com',
  sameAs: [
    'https://www.facebook.com/sawssonipat/',
    'https://www.instagram.com/sawssonipat/',
    'https://www.youtube.com/@sawssonipat',
  ],
};

const SEO = ({
  slug,
  pageSlug,
  title,
  description,
  keywords,
  ogImage,
  canonical,
  noIndex = false,
  schema,
  article,
}) => {
  const location = useLocation();
  const [adminSeo, setAdminSeo] = useState(null);

  // Determine lookup slug: prop slug -> prop pageSlug -> pathname without leading slash
  const lookupSlug =
    slug ||
    pageSlug ||
    (location.pathname === '/'
      ? 'home'
      : location.pathname.replace(/^\/+|\/+$/g, '').replace(/\//g, '-'));

  useEffect(() => {
    let isMounted = true;
    if (lookupSlug) {
      API.get(`/seo/${encodeURIComponent(lookupSlug)}`)
        .then((res) => {
          if (isMounted && res.data?.data) {
            setAdminSeo(res.data.data);
          }
        })
        .catch(() => {
          // Fall back gracefully if not found in admin SEO database
        });
    }
    return () => {
      isMounted = false;
    };
  }, [lookupSlug]);

  // Priority Resolution: Admin API -> Prop -> Default
  const resolvedRawTitle = adminSeo?.meta_title || title || '';
  const finalTitle = resolvedRawTitle
    ? resolvedRawTitle.includes(SITE_NAME) || resolvedRawTitle.includes('St. Andrews')
      ? resolvedRawTitle
      : `${resolvedRawTitle} | ${SITE_NAME}`
    : DEFAULT_SITE_TITLE;

  const finalDesc =
    adminSeo?.meta_description || description || DEFAULT_DESC;

  const finalKeywords =
    adminSeo?.meta_keywords || keywords || DEFAULT_KEYWORDS;

  // Resolve Image: Admin API -> Prop -> Fallback logo
  let rawImage = adminSeo?.og_image || ogImage;
  let finalImage = DEFAULT_IMAGE;
  if (rawImage) {
    if (rawImage.startsWith('http://') || rawImage.startsWith('https://')) {
      finalImage = rawImage;
    } else if (rawImage.startsWith('/uploads') || rawImage.startsWith('uploads/')) {
      finalImage = `${SITE_URL}/${rawImage.replace(/^\/+/, '')}`;
    } else {
      finalImage = `${UPLOADS_URL}/${rawImage.replace(/^\/+/, '')}`;
    }
  }

  // Canonical URL
  const currentPath = location.pathname === '/' ? '' : location.pathname;
  const finalCanonical =
    adminSeo?.canonical_url || canonical || `${SITE_URL}${currentPath}`;

  const isNoIndex = Boolean(adminSeo?.no_index ?? noIndex);
  const isArticle = Boolean(article);

  // Schema generation
  const customSchemas = Array.isArray(schema)
    ? schema
    : schema
    ? [schema]
    : [];

  const allSchemas = [DEFAULT_SCHOOL_SCHEMA, ...customSchemas];

  return (
    <Helmet>
      {/* Standard Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDesc} />
      {finalKeywords && <meta name="keywords" content={finalKeywords} />}
      <link rel="canonical" href={finalCanonical} />
      {isNoIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      {/* Open Graph Meta Properties */}
      <meta property="og:type" content={isArticle ? 'article' : 'website'} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDesc} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:url" content={finalCanonical} />
      <meta property="og:locale" content="en_US" />

      {/* Article Properties (for Blogs & Articles) */}
      {isArticle && article.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {isArticle && article.modifiedTime && (
        <meta property="article:modified_time" content={article.modifiedTime} />
      )}
      {isArticle && article.author && (
        <meta property="article:author" content={article.author} />
      )}
      {isArticle &&
        Array.isArray(article.tags) &&
        article.tags.map((tag, idx) => (
          <meta key={`art-tag-${idx}`} property="article:tag" content={String(tag).trim()} />
        ))}

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDesc} />
      <meta name="twitter:image" content={finalImage} />

      {/* JSON-LD Structured Data */}
      {allSchemas.map((schemaObj, idx) => (
        <script key={`schema-${idx}`} type="application/ld+json">
          {JSON.stringify(schemaObj)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
